import type { ReactElement, ReactNode } from 'react';
import { useRef, useMemo } from 'react';

import type { InMemoryCache, DocumentNode, ErrorLike } from '@apollo/client';
import {
  ApolloClient, ApolloLink,
} from '@apollo/client';
import type { TypePolicies } from '@apollo/client/cache';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import {
  ApolloProvider
} from '@apollo/client/react'
import { useAuth } from '@virtuslab/react-oauth2';
import UploadLink from 'apollo-upload-client/UploadHttpLink.mjs';
import { OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import isNil from 'lodash/isNil';
import { v4 } from 'uuid';

import { createScalarTransformLink } from '../../../schema/scalarTransformLink';
import { getAuthHeader } from '../../../services/auth';
import { isEmpty } from '../../../services/checks';
import useOnRouteChange from '../../../services/hooks/useOnRouteChange';
import type { Nullable } from '../../../services/object';

import { useFeatureFlagsContext } from '../FeatureFlagsContext';

type ApolloConfig = Readonly<{
  apiUrl: string;
  isDev?: boolean;
}>;

type Props = Readonly<{
  children: ReactNode;
  cache: InMemoryCache;
  config: ApolloConfig;
  typeDefs: DocumentNode;
  scalarTypePolicies: TypePolicies;
  onUnauthorized: () => void;
}>;

const formatError = (error: NonNullable<ErrorLike>): string => `[GraphQL Error]: Message: ${error.message}. ${error.name}`;

const logError: ErrorLink.ErrorHandler = (opts) => {
  if (!isNil(opts.error)) {
    // eslint-disable-next-line no-console
    console.error(formatError(opts.error))
  }
};

const hasUnauthorizedError = (networkError: Nullable<ErrorLike>): boolean => {
  if (isNil(networkError)) {
    return false;
  }

  return [networkError.name, networkError.message].some((text) => /unauthorized/i.test(text));
};

const toWebSocketUrl = (graphqlUrl: string): string => new URL(graphqlUrl, window.location.href)
  .href
  .replace(/^http/, 'ws');

const SetupApollo = ({
  children, cache, config, scalarTypePolicies, onUnauthorized,
}: Props): ReactElement => {
  const auth = useAuth();

  const onUnauthorizedRef = useRef(onUnauthorized);

  const { clearCachePeriodically } = useFeatureFlagsContext();

  const client: ApolloClient = useMemo(() => {
    const graphqlUri = `${config.apiUrl}/api/graphql`;

    const link = ApolloLink.from([
      new ErrorLink((response) => {
        logError(response);

        // Rethrowing here reaches rxjs as an uncaught error and stops useSubscription ever seeing it;
        // redirecting is worse still, since a socket closed 4401 would eject the user mid-work.
        if (response.operation.operationType === OperationTypeNode.SUBSCRIPTION) {
          return;
        }

        if (hasUnauthorizedError(response.error)) {
          onUnauthorizedRef.current();
          return;
        }

        const errorMessage = formatError(response.error)

        if (!isEmpty(errorMessage)) {
          throw Error(errorMessage);
        }
      }),
      new SetContextLink(async (prevContext) => {
        // get the authentication header
        const authHeader = await getAuthHeader(auth);
        const { headers } = prevContext as { headers: Record<string, string> };

        if (isNil(authHeader)) {
          window.location.reload();
        }

        return {
          headers: {
            ...headers,
            ...authHeader,
            'X-Trace-Id': v4(),
          },
        };
      }),
      createScalarTransformLink(scalarTypePolicies),
      ApolloLink.split(
        ({ operationType }) => operationType === OperationTypeNode.SUBSCRIPTION,
        new GraphQLWsLink(createClient({
          url: toWebSocketUrl(graphqlUri),
          // A browser cannot set headers on a WebSocket handshake, so the token travels in the
          // connection_init payload under the name of the header it replaces.
          connectionParams: async () => await getAuthHeader(auth) ?? {},
        })),
        // TODO add batch query back when possible #187
        new UploadLink({
          uri: graphqlUri,
        }) as unknown as ApolloLink,
      ),
    ]);

    return new ApolloClient({
      devtools: {
        enabled: config.isDev
      },
      link,
      cache,
    });
  }, [auth, cache, config, scalarTypePolicies]);

  useOnRouteChange({
    listener: () => {
      if (clearCachePeriodically) {
        client.cache.reset();
      }
    },
  });

  // remember to client.resetStore() on logout.
  return (
    <ApolloProvider client={client}>
      {children}
    </ApolloProvider>
  );
};

export default SetupApollo;
